import type { RevisionedLineItemType } from '@weft/types';
import { isNullOrUndefined, raiseUnreachable, validateNotEmptyString } from '@weft/utils';
import VyDialog from 'apps/app/src/components/new-design/feedback/VyDialog';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import type { PropsWithChildren } from 'react';
import React from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { QueryKey } from 'react-query';
import { useQueryClient } from 'react-query';
import { useCreateRevisionedLineItem } from '../../../../hooks/requests/use-create-revisioned-line-item';
import { useUpdateRevisionedLineItem } from '../../../../hooks/requests/use-update-revisioned-line-item';
import type { RevisionedLineItemDialogFormType } from './revisioned-line-item-dialog-forms/revisioned-line-item-dialog-forms-types/revisioned-line-item-dialog-type';

export const RevisionedLineItemDialogSubmitActions = {
  Create: 'Create',
  Update: 'Update',
} as const;

export type RevisionedLineItemDialogSubmitAction = typeof RevisionedLineItemDialogSubmitActions[keyof typeof RevisionedLineItemDialogSubmitActions];

type RevisionedLineItemDialogFormProps<FormSchema extends RevisionedLineItemDialogFormType> = PropsWithChildren<{
  revisionedLineItemId?: string;
  title?: string;
  open: boolean;
  onClose?: () => void;
  revisionedLineItemType: RevisionedLineItemType;
  invoiceId: string;
  form: UseFormReturn<FormSchema>;
  isDisabled?: boolean;
  submitAction?: RevisionedLineItemDialogSubmitAction;
  submitButtonLabel?: string;
  queryKeyToInvalidate?: QueryKey;
}>;

export const RevisionedLineItemDialogForm = <FormSchema extends RevisionedLineItemDialogFormType>({
  revisionedLineItemId,
  title,
  open,
  onClose,
  revisionedLineItemType,
  invoiceId,
  form,
  isDisabled = false,
  children,
  submitAction = RevisionedLineItemDialogSubmitActions.Create,
  submitButtonLabel = 'Create',
  queryKeyToInvalidate,
}: RevisionedLineItemDialogFormProps<FormSchema>) => {
  const queryClient = useQueryClient();
  const createFixedRevisionedLineItemMutation = useCreateRevisionedLineItem();
  const updateFixedRevisionedLineItemMutation = useUpdateRevisionedLineItem();

  const onSubmit = form.handleSubmit(async ({ description, ...data }) => {
    if (isNullOrUndefined(data)) {
      return undefined;
    }

    switch (submitAction) {
      case RevisionedLineItemDialogSubmitActions.Create:
        await runMutation(
          createFixedRevisionedLineItemMutation,
          {
            type: revisionedLineItemType,
            invoiceId,
            description,
            ...data,
          }, 
          {
            pending: 'Creating line item...',
            success: 'Line item created',
            error: 'Failed to create line item',
          },
          {
            onSuccess: () => queryClient.invalidateQueries(queryKeyToInvalidate),
          });
        break;
      case RevisionedLineItemDialogSubmitActions.Update:
        validateNotEmptyString(revisionedLineItemId, 'revisionedLineItemId');
        await runMutation(
          updateFixedRevisionedLineItemMutation,
          {
            revisionedLineItemId,
            updateRevisionedLineItemRequest: {
              type: revisionedLineItemType,
              invoiceId,
              description,
              ...data,
            },
          },
          {
            pending: 'Updating line item...',
            success: 'Line item updated',
            error: 'Failed to update line item',
          },
          {
            onSuccess: () => queryClient.invalidateQueries(queryKeyToInvalidate),
          });
        break;
      default:
        raiseUnreachable(submitAction);
    }

    return onClose?.();
  });

  const handleClose = () => {
    form.reset();
    form.clearErrors();
    onClose?.();
  };

  const ctaDisabled = isDisabled || createFixedRevisionedLineItemMutation.isLoading || updateFixedRevisionedLineItemMutation.isLoading;

  return (
    <VyDialog 
      open={open}
      onClose={handleClose}
      label={title ?? ''}
      labelDescription="Changes and updates will apply to this invoice only, and will not affect future invoices."
      cancelButtonText="Cancel"
      ctaText={submitButtonLabel}
      ctaDisabled={ctaDisabled}
      onSubmit={onSubmit}
    >
      {children}
    </VyDialog>
  );
};
